/**
 * TransferOwnershipModal Component
 *
 * Modal for transferring agency ownership to another team member.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import LytModal from 'src/components/ui-core/modal/LytModal';
import LytButton from 'src/components/library/button/LytButton';
import LytTextField from 'src/components/library/text-field/LytTextField';
import {
    getTransferOwnershipLabel,
    getTransferOwnershipModalTitle,
    getTransferOwnershipNewOwnerEmailLabel,
    getTransferOwnershipEmailRequiredError,
    getTransferOwnershipEmailInvalidError,
    getTransferOwnershipSameOwnerError,
} from 'src/constants/copy/team.copy';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from 'src/components/styled/theme/typography-utils';

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const BodyText = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;

const ButtonGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getUserDisplayName = (user) => {
    const firstName = user?.firstname || user?.first_name || user?.firstName || '';
    const lastName = user?.lastname || user?.last_name || user?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user?.username || user?.email || 'the current owner';
};

/**
 * TransferOwnershipModal - Collects the new owner email and confirms transfer.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} [props.closeModal]
 * @param {Object} props.owner - Current agency owner row data
 * @param {Function} [props.onConfirm] - Async callback with { new_owner_email }
 */
const TransferOwnershipModal = ({
    open = false,
    closeModal = undefined,
    owner = {},
    onConfirm = undefined,
}) => {
    const [newOwnerEmail, setNewOwnerEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            setNewOwnerEmail('');
            setEmailError('');
            setLoading(false);
        }
    }, [open]);

    if (!open) return null;

    const ownerName = getUserDisplayName(owner);

    const handleCancel = () => {
        closeModal?.({ confirmed: false });
    };

    const validateEmail = (value) => {
        const trimmed = value.trim();
        if (!trimmed) {
            return getTransferOwnershipEmailRequiredError();
        }
        if (!EMAIL_PATTERN.test(trimmed)) {
            return getTransferOwnershipEmailInvalidError();
        }
        if (owner?.email && trimmed.toLowerCase() === owner.email.trim().toLowerCase()) {
            return getTransferOwnershipSameOwnerError();
        }
        return '';
    };

    const handleConfirm = async () => {
        const validationError = validateEmail(newOwnerEmail);
        if (validationError) {
            setEmailError(validationError);
            return;
        }

        setLoading(true);
        try {
            if (onConfirm) {
                await onConfirm({
                    new_owner_email: newOwnerEmail.trim(),
                });
            }
            closeModal?.({
                confirmed: true,
                new_owner_email: newOwnerEmail.trim(),
            });
        } catch (error) {
            console.error('Error during ownership transfer:', error);
            setLoading(false);
        }
    };

    const footerActions = (
        <ButtonGroup>
            <LytButton
                type="secondary"
                onClick={handleCancel}
                disabled={loading}
            >
                Cancel
            </LytButton>
            <LytButton
                type="primary"
                onClick={handleConfirm}
                loading={loading}
                disabled={loading}
            >
                {getTransferOwnershipLabel()}
            </LytButton>
        </ButtonGroup>
    );

    return (
        <LytModal
            open={open}
            title={getTransferOwnershipModalTitle()}
            onClose={handleCancel}
            actions={footerActions}
            size="small"
        >
            <ModalContent>
                <BodyText>
                    Transfer ownership from {ownerName} to another team member.
                    The new owner will receive full ownership privileges for this agency.
                </BodyText>
                <LytTextField
                    name="transfer-ownership-email"
                    type="email"
                    label={getTransferOwnershipNewOwnerEmailLabel()}
                    initialValue={newOwnerEmail}
                    onChange={(value) => {
                        setNewOwnerEmail(value);
                        if (emailError) {
                            setEmailError('');
                        }
                    }}
                    errorText={emailError}
                    required
                    aria-label={getTransferOwnershipNewOwnerEmailLabel()}
                />
            </ModalContent>
        </LytModal>
    );
};

TransferOwnershipModal.propTypes = {
    open: PropTypes.bool,
    closeModal: PropTypes.func,
    owner: PropTypes.shape({
        email: PropTypes.string,
        username: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
    }),
    onConfirm: PropTypes.func,
};

export default TransferOwnershipModal;
